import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const FilterContext = createContext(null);

function defaultRange() {
  // Last 30 days as reasonable default; will be overridden by data range after first load.
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 29);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { start: iso(start), end: iso(end) };
}

export function FilterProvider({ children }) {
  const initial = defaultRange();
  const [start, setStart] = useState(initial.start);
  const [end, setEnd] = useState(initial.end);
  const [line, setLine] = useState("all");
  const [subsystem, setSubsystem] = useState("all");
  const [meta, setMeta] = useState({ lines: [], subsystems: [], date_min: null, date_max: null, last_updated: null });
  const [initialized, setInitialized] = useState(false);

  const loadFilters = useCallback(async () => {
    try {
      const { data } = await api.get("/metrics/filters");
      setMeta(data);
      if (!initialized && data.date_min && data.date_max) {
        // Default filter = last month of data
        const maxD = new Date(data.date_max);
        const minD = new Date(maxD);
        minD.setDate(maxD.getDate() - 29);
        const iso = (d) => d.toISOString().slice(0, 10);
        const newStart = iso(minD) < data.date_min ? data.date_min : iso(minD);
        setStart(newStart);
        setEnd(data.date_max);
        setInitialized(true);
      }
    } catch (e) {
      console.error("Failed to load filter metadata (/metrics/filters):", e);
    }
  }, [initialized]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  const reset = () => {
    if (meta.date_min && meta.date_max) {
      const maxD = new Date(meta.date_max);
      const minD = new Date(maxD);
      minD.setDate(maxD.getDate() - 29);
      const iso = (d) => d.toISOString().slice(0, 10);
      setStart(iso(minD) < meta.date_min ? meta.date_min : iso(minD));
      setEnd(meta.date_max);
    } else {
      const d = defaultRange();
      setStart(d.start);
      setEnd(d.end);
    }
    setLine("all");
    setSubsystem("all");
  };

  const params = { start, end, line, subsystem };

  return (
    <FilterContext.Provider value={{ start, end, line, subsystem, setStart, setEnd, setLine, setSubsystem, params, meta, reset, refreshMeta: loadFilters }}>
      {children}
    </FilterContext.Provider>
  );
}

export const useFilters = () => useContext(FilterContext);
